import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router";
import { useCheckIn } from "@/api/queries";
import { actionTitle, fhsaMoveReason, fhsaWarning } from "@/plan-logic/plan-view/actionText";
import { FIX_FIRST_TIME_BUYER_URL } from "@/app/pages/useFamilySave";
import { MoneyField, SelectField } from "@/components/fields";
import { useToast } from "@/components/Toast";
import type { FamilyDocument } from "@/generated/backend/v1/family";
import type { Action } from "@/generated/core/v1/actions";
import type { FinancialPlanData } from "@/generated/core/v1/financial_plan";
import type { Family } from "@/generated/core/v1/family";
import { addMonths, compareDates, formatDate, isSet, type PDate } from "@/plan-logic/lib/dates";
import { money } from "@/plan-logic/lib/format";
import {
  buildEntityIndex,
  checkInActions,
  flattenAction,
  isUnconfirmedFhsaTransfer,
} from "@/plan-logic/plan-view";
import {
  balancesToVerify,
  buildAnswers,
  type CheckInShortfall,
  checkInErrorText,
  checkInShortfall,
  clearDraft,
  dismissedToday,
  dismissForToday,
  doesntAddUpText,
  loadDraft,
  saveDraft,
  UPDATE_DATA_SKIPPING_CHECK_IN,
  rateOptions,
  type Answer,
} from "./checkin";

/**
 * Whether there is anything to check in about: the household's facts predate today, and the plan
 * recommended actions since then or balances are stale. Once a check-in is saved the facts are as
 * of today, so this is false even before the new plan arrives.
 */
export function checkInPending(family: Family, abi: FinancialPlanData, today: PDate): boolean {
  if (isSet(family.factsAsOf) && compareDates(family.factsAsOf, today) >= 0) return false;
  return (
    checkInActions(abi, today).some((a) => flattenAction(a).length > 0) ||
    balancesToVerify(family, today).length > 0
  );
}

/**
 * Whether the plan shown is out of date: "today" is past the plan's start (its own "today") by a
 * month or more, or by enough that the app offers a check-in (`checkInPending`). Its dates and
 * figures then count from its start, not from today.
 */
export function planIsStale(
  family: Family,
  abi: FinancialPlanData,
  planStart: PDate,
  today: PDate,
): boolean {
  if (compareDates(today, planStart) <= 0) return false;
  return compareDates(addMonths(planStart, 1), today) <= 0 || checkInPending(family, abi, today);
}

/** Whether the shell should open the check-in: pending, and not put off today. */
export function useCheckInDue(
  doc: FamilyDocument | null | undefined,
  abi: FinancialPlanData | undefined,
  today: PDate,
): boolean {
  return useMemo(() => {
    if (!doc?.family || !abi || dismissedToday(today)) return false;
    return checkInPending(doc.family, abi, today);
  }, [doc, abi, today]);
}

/** What the plan asked for: the contribution rate for a group RRSP action, else the amount. */
const planned = (a: Action, amount: number) =>
  a.actionType.oneofKind === "setGroupRrspContributionAction"
    ? a.actionType.setGroupRrspContributionAction.employeeContributionRate
    : amount;

/**
 * The prototype's check-in modal ("What did you manage to do?"): the plan's recommended actions
 * between the facts date and today, each "Did it / Not yet" plus the actual amount, and a
 * "verify your balances" item when balances are stale. Saving queues a re-plan.
 */
export function CheckInModal({
  doc,
  abi,
  today,
  onClose,
}: {
  doc: FamilyDocument;
  abi: FinancialPlanData;
  today: PDate;
  onClose: () => void;
}) {
  const family = doc.family!;
  const index = useMemo(() => buildEntityIndex(family, abi), [family, abi]);
  const actions = useMemo(() => checkInActions(abi, today), [abi, today]);
  // Opening an account with no money moved has nothing to answer, so it isn't listed or counted.
  const shown = useMemo(() => actions.filter((a) => flattenAction(a).length > 0), [actions]);
  const toVerify = useMemo(() => balancesToVerify(family, today), [family, today]);
  // Answers kept from a visit that went to Update Data (same family version and day).
  const [draft] = useState(() => loadDraft(doc.version, today));
  const [answers, setAnswers] = useState<Record<number, Answer>>(() =>
    Object.fromEntries(
      Object.entries(draft?.answers ?? {}).filter(([id]) => shown.some((a) => a.id === +id)),
    ),
  );
  const [balances, setBalances] = useState<Record<string, number | undefined>>(
    () => draft?.balances ?? {},
  );
  const [verify, setVerify] = useState<boolean | undefined>(draft?.verify);
  const [error, setError] = useState<string | null>(null);
  const [short, setShort] = useState<CheckInShortfall | null>(null);
  const navigate = useNavigate();
  const checkIn = useCheckIn();
  const toast = useToast();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && close();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const close = () => {
    dismissForToday(today);
    onClose();
  };

  const n =
    Object.values(answers).filter((a) => a.did !== undefined).length +
    Object.values(balances).filter((v) => v !== undefined).length;

  const save = async () => {
    if (!n) return close();
    setError(null);
    setShort(null);
    try {
      await checkIn.mutateAsync({
        answers: buildAnswers(actions, answers, verify ? balances : {}, today),
        expectedVersion: doc.version,
      });
      dismissForToday(today);
      clearDraft();
      toast("Thanks. Abi is folding that into your plan now.");
      onClose();
    } catch (e) {
      const ranShort = checkInShortfall(e);
      if (ranShort) setShort(ranShort);
      else setError(checkInErrorText(e));
    }
  };

  // The answers don't add up: keep them for the session and go to Update Data.
  const updateData = () => {
    saveDraft(doc.version, today, { answers, balances, verify });
    close();
    navigate(UPDATE_DATA_SKIPPING_CHECK_IN.to, { state: UPDATE_DATA_SKIPPING_CHECK_IN.state });
  };

  const set = (id: number, patch: Answer) =>
    setAnswers((a) => ({ ...a, [id]: { ...a[id], ...patch } }));

  return (
    <div
      className="ciwrap"
      role="dialog"
      aria-modal="true"
      aria-labelledby="ci-title"
      data-testid="checkin"
    >
      <div className="cimodal">
        <div className="cihead">
          <div>
            <div className="eyebrow">Before you start</div>
            <h2 id="ci-title" style={{ fontSize: 20, marginTop: 3 }}>
              What did you manage to do?
            </h2>
          </div>
          <button type="button" className="cix" onClick={close} aria-label="Close">
            &times;
          </button>
        </div>
        <p className="mut" style={{ margin: "0 0 16px", maxWidth: "60ch" }}>
          {shown.length
            ? `${shown.length === 1 ? "One thing was" : `${shown.length} things were`} on your list since ${formatDate(abi.meta?.simulatedFrom)}. Tell us what happened and we'll fold it into the plan.`
            : "Your balances haven't been checked in a while. Tell us where they are today and we'll fold it into the plan."}
        </p>
        <div id="ci-body">
          {shown.map((a) => {
            const f = flattenAction(a)[0];
            const ans = answers[a.id] ?? {};
            return (
              <div className="cirow" key={a.id} data-testid={`ci-${a.id}`}>
                <div style={{ flex: 1 }}>
                  <b>{actionTitle(f, index)}</b>
                  <span className="mut">was due {formatDate(a.date)}</span>
                  {fhsaMoveReason(f, index) && (
                    <span className="mut" data-testid={`ci-${a.id}-why`}>
                      {fhsaMoveReason(f, index)}
                    </span>
                  )}
                  {isUnconfirmedFhsaTransfer(a) && (
                    <span className="ciwarn" role="note" data-testid={`ci-${a.id}-fhsa`}>
                      <b>Warning!</b> {fhsaWarning(f, index)}{" "}
                      <Link to={FIX_FIRST_TIME_BUYER_URL} onClick={onClose}>
                        Update your data
                      </Link>
                    </span>
                  )}
                </div>
                <div className="ciyn">
                  <button
                    type="button"
                    className={`ynb${ans.did === true ? " sel" : ""}`}
                    onClick={() =>
                      set(a.id, { did: true, amount: ans.amount ?? planned(a, f.amount) })
                    }
                  >
                    Did it
                  </button>
                  <button
                    type="button"
                    className={`ynb${ans.did === false ? " sel" : ""}`}
                    onClick={() => set(a.id, { did: false })}
                  >
                    Not yet
                  </button>
                </div>
                {ans.did && a.actionType.oneofKind === "setGroupRrspContributionAction" && (
                  <div className="ciask">
                    <SelectField
                      label="What rate did you set?"
                      value={
                        ans.amount ??
                        a.actionType.setGroupRrspContributionAction.employeeContributionRate
                      }
                      options={rateOptions(
                        a.actionType.setGroupRrspContributionAction.employeeContributionRate,
                      )}
                      onChange={(amount) => set(a.id, { amount })}
                      style={{ margin: 0 }}
                    />
                  </div>
                )}
                {ans.did && a.actionType.oneofKind !== "setGroupRrspContributionAction" && (
                  <div className="ciask">
                    <MoneyField
                      label="How much did you actually move?"
                      value={ans.amount}
                      onChange={(amount) => set(a.id, { amount })}
                      placeholder={money(f.amount)}
                      style={{ margin: 0 }}
                    />
                  </div>
                )}
              </div>
            );
          })}
          {toVerify.length > 0 && (
            <div className="cirow" data-testid="ci-balances">
              <div style={{ flex: 1 }}>
                <b>Verify your account balances</b>
                <span className="mut">
                  The last ones we have are from {formatDate(family.factsAsOf)}.
                </span>
              </div>
              <div className="ciyn">
                <button
                  type="button"
                  className={`ynb${verify ? " sel" : ""}`}
                  onClick={() => setVerify(true)}
                >
                  Update them
                </button>
                <button
                  type="button"
                  className={`ynb${verify === false ? " sel" : ""}`}
                  onClick={() => setVerify(false)}
                >
                  Later
                </button>
              </div>
              {verify && (
                <div className="ciask" style={{ display: "grid", gap: 10 }}>
                  {toVerify.map((b) => (
                    <MoneyField
                      key={b.path}
                      label={b.label}
                      value={balances[b.path]}
                      onChange={(v) => setBalances((x) => ({ ...x, [b.path]: v }))}
                      placeholder={b.current !== undefined ? money(b.current) : "Balance today"}
                      style={{ margin: 0 }}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
          <p className="hint">
            Telling us what actually happened is what keeps the plan honest. Abi recalculates around
            whatever you did, not whatever it suggested.
          </p>
        </div>
        {short && (
          <div className="cishort" role="alert" data-testid="ci-doesnt-add-up">
            <p>{doesntAddUpText(short)}</p>
            <button type="button" className="btn" onClick={updateData} data-testid="ci-update-data">
              Update your data
            </button>
          </div>
        )}
        {error && (
          <p className="wp-form-err" role="alert">
            {error}
          </p>
        )}
        <button
          type="button"
          className="btn"
          id="ci-cta"
          onClick={save}
          disabled={checkIn.isPending}
          style={{ marginTop: 18, width: "100%" }}
          data-testid="ci-save"
        >
          {checkIn.isPending
            ? "Saving…"
            : n
              ? `Save ${n} update${n === 1 ? "" : "s"} and recalculate`
              : "Nothing yet, remind me tomorrow"}
        </button>
      </div>
    </div>
  );
}
